import React from 'react';
import * as Sentry from '@sentry/react';
import ErrorRepresentation from './ErrorRepresentation/ErrorRepresentation';
import {FallbackRender} from '@sentry/react/types/errorboundary';
import app from '@src/helpers/app';
import {Scope} from '@sentry/browser';

type Props = {
    children: React.ReactNode,
    dontFlushContent?: boolean,
    fallbackRender?: React.ComponentType<{ error: Error }>,
} & Pick<
        React.ComponentProps<typeof ErrorRepresentation>,
        'size'
        | 'outsideContainer'
        | 'withoutErrorName'
        | 'withoutStackTrace'
> & ({
    dontReport: true,
    contentKey: string,
    dontDisplayError?: boolean,
} | {
    dontReport?: false,
    contentKey?: undefined,
})

export default class ErrorBoundary extends React.Component<Props, { error?: Error | null }> {
    state = {
        error: undefined,
    };

    constructor(props: Props) {
        super(props);

        this.fallbackRender = this.fallbackRender.bind(this);
        this.beforeCapture = this.beforeCapture.bind(this);
    }

    getSnapshotBeforeUpdate(prevProps: Readonly<Props>): { haveToFlushError: boolean } {
        if (prevProps.contentKey !== this.props.contentKey && this.state.error != null) {
            return {haveToFlushError: true};
        }

        return {haveToFlushError: false};
    }

    componentDidUpdate(
            prevProps: Readonly<Props>,
            prevState: Readonly<{ error?: Error | null }>,
            snapshot?: { haveToFlushError?: boolean }
    ) {
        if (snapshot?.haveToFlushError === true) {
            this.setState({error: undefined});
        }
    }

    static getDerivedStateFromError(error: Error) {
        return {error};
    }

    render() {
        if (this.props.dontReport === true) {
            return this.fallbackRender({error: this.state.error});
        }

        return <Sentry.ErrorBoundary
                fallback={this.fallbackRender}
                beforeCapture={this.beforeCapture}
        >
            {this.props.children}
        </Sentry.ErrorBoundary>;
    }

    protected fallbackRender({error}: Partial<Parameters<FallbackRender>[0]>) {
        const Representation = this.props.fallbackRender ?? ErrorRepresentation;

        if (this.props.dontReport === true && this.props.dontDisplayError === true) {
            return <>{this.props.children}</>;
        }

        if (error && this.props.dontFlushContent !== true) {
            return <Representation
                    error={error}
                    outsideContainer={this.props.outsideContainer}
                    withoutErrorName={this.props.withoutErrorName}
                    withoutStackTrace={this.props.withoutStackTrace}
                    size={this.props.size}
            />;
        }

        return <>
            {error ? <Representation error={error}/> : null}

            {this.props.children}
        </>;
    }

    protected beforeCapture(scope: Scope, error: Error | null): void {
        if (error) {
            app('errorHandler').handle(error);
        }
    }
}
